/**
 * CompanyDataTab — F12.M Etap D Part 2 (Companies fullscreen, B1).
 *
 * Silnik formatek (keystone unifikacja-formatek — mirror `ContractorDataTab.tsx`): 14 pól
 * core routowanych przez `renderField(fieldId, settings)` z propsów, dostarczany przez
 * rodzica (`CompanyDetail`) z `useKartotekaRenderField(companyEngineConfig, runtime)`. Ten
 * komponent pobiera TYLKO aktywny layout modułu Firmy przez
 * `useSystemModuleActiveLayout('companies')` (ten sam hook co rodzic — honoruje layout
 * przypięty w pozycji menu `?mi=`), iteruje po sekcjach z
 * `layout_config.tabs[0].sections` (zakładka „Dane" type='fields') i dla każdego elementu
 * typu 'field' deleguje do `renderField` (silnik sam rozstrzyga core-vs-ext, metadane pola,
 * gcrecord, custom widget vs natywna delegacja) — DataTab nie zna już `SystemModuleField`/
 * `fieldsQ`.
 *
 * Różnice od kontrahenta (Firmy są prostsze):
 *  - 14 pól (vs 26+ u Kontrahentów)
 *  - brak sekcji GDPR (Firmy nie są subject GDPR)
 *  - brak TEXT[] outliers (tags/pkdCodes) — Firmy nie używają, więc brak hardkodowanej
 *    sekcji na końcu (Kontrahent ma „Tagi i kody PKD")
 *  - brak NIP onBlur check (Firmy obecnie nie mają dedykowanego check-nip endpointu)
 *
 * Save flow w CompanyDetail (rodzic): JEDEN POST/PATCH /companies z `extValues` w tym samym
 * body — bez osobnego PUT ext-fields.
 */

import { Fragment, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import { type ContextRenderers, PLACEHOLDER_TYPES } from '@/components/form/RecursiveFormRenderer';
import { isFieldElementVisible, StyledFieldSlot } from '@/components/form/styled-field';
import { Section } from '@/components/kartoteki/CoreFieldLayout';
import { Skeleton } from '@/components/Skeleton';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { loadTabsFromConfig } from '@/pages/system-modules/layout-shape-adapter';

const MODULE_SLUG = 'companies';

// ─── Type exports (używane w CompanyDetail) ────────────────────────────────

export interface CompanyFormData {
  legalName: string;
  shortName: string | null;
  taxId: string | null;
  vatEu: string | null;
  regon: string | null;
  krs: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  brandColor: string | null;
  defaultVatRate: string | null;
  defaultPaymentTermsDays: number | null;
  isPrimary: boolean;
  isActive: boolean;
  /** Grupy rekordu — kody słownika `company_groups`, WIELE na rekord (migracja 0118). */
  groups: string[];
}

export const EMPTY_COMPANY_FORM: CompanyFormData = {
  legalName: '',
  shortName: null,
  taxId: null,
  vatEu: null,
  regon: null,
  krs: null,
  email: null,
  phone: null,
  website: null,
  brandColor: null,
  defaultVatRate: null,
  defaultPaymentTermsDays: null,
  isPrimary: false,
  isActive: true,
  groups: [],
};

/**
 * Słownik slug→stateKey dla pól core firmy (mirror `CONTRACTOR_CORE_FIELD_SLUGS`).
 * Slugi = `coreFields` modułu `companies` w `apps/api/lib/seed-system-modules.ts`. Służy do
 * budowy `Record<slug, wartość>` na potrzeby warunków widoczności pól/bloków z kreatora —
 * ORAZ (silnik formatek) do budowy `companyEngineConfig.fields` z tego samego źródła prawdy
 * (mirror `CONTRACTOR_CORE_FIELD_SLUGS` w `contractorEngineConfig.tsx`).
 */
export const COMPANY_CORE_FIELD_SLUGS: Record<string, keyof CompanyFormData> = {
  legal_name: 'legalName',
  short_name: 'shortName',
  tax_id: 'taxId',
  vat_eu: 'vatEu',
  regon: 'regon',
  krs: 'krs',
  email: 'email',
  phone: 'phone',
  website: 'website',
  brand_color: 'brandColor',
  default_vat_rate: 'defaultVatRate',
  default_payment_terms_days: 'defaultPaymentTermsDays',
  is_primary: 'isPrimary',
  is_active: 'isActive',
  groups: 'groups',
};

/** Buduje `Record<slug, wartość>` z formularza firmy (do oceny warunków widoczności). */
export function buildCompanyCoreRecordValues(form: CompanyFormData): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [slug, key] of Object.entries(COMPANY_CORE_FIELD_SLUGS)) {
    result[slug] = form[key];
  }
  return result;
}

// ─── Types dla fetch'owanych danych ─────────────────────────────────────────

interface LayoutElement {
  id: string;
  type: string;
  fieldId?: string;
  /** Ukrycie przez nakładkę per-tenant (systemowy layout) — pomijane na karcie. */
  hidden?: boolean;
  settings?: Record<string, unknown>;
}
interface LayoutSection {
  id: string;
  title: string | null;
  columns?: number;
  elements: LayoutElement[];
}

// ─── Props ─────────────────────────────────────────────────────────────────

export interface CompanyDataTabProps {
  /**
   * Renderery elementów zależnych od kontekstu (company_members_view/…), te same, które
   * CompanyDetail przekazuje do RecursiveFormRenderer. Bez nich taki element postawiony w
   * zakładce „Dane" renderuje się jako martwy placeholder z generycznego rejestru. Most do
   * czasu przejścia kartoteki w całości na silnik (mirror `ContractorDataTabProps`).
   *
   * UWAGA (różnica od `ContractorDataTabProps`): brak `form`/`onChange`/`editable` — Kontrahent
   * je zatrzymuje, bo ma hardkodowaną sekcję TEXT[] outliers (tags/pkdCodes) poza silnikiem,
   * która potrzebuje bezpośredniego dostępu do stanu. Firmy nie mają takiego outliera (żadne
   * pole nie renderuje się poza `renderField`), więc te trzy propsy byłyby martwe — pominięte
   * celowo, nie przez przeoczenie.
   */
  contextRenderers?: ContextRenderers;
  /**
   * Renderuje pole core/ext po id — dostarczany przez rodzica z
   * `useKartotekaRenderField(companyEngineConfig, runtime)`. Silnik sam rozstrzyga
   * core-vs-ext, metadane pola (gcrecord/isCore/isRequired) i custom widget vs natywną
   * delegację — ten komponent tylko iteruje po layoucie.
   */
  renderField: (fieldId: string, settings?: Record<string, unknown>) => ReactNode;
  /**
   * Wartości formularza keyed po slugu pola (core + ext) — do oceny warunków widoczności
   * pól/bloków (`settings.conditionalField/Operator/Value`). Rodzic (`CompanyDetail`) buduje
   * ją z `buildCompanyCoreRecordValues(form)` + `extValues`. Default `{}` — bez niej warunki
   * traktujemy jak „zawsze widoczne".
   */
  formData?: Record<string, unknown>;
}

// ─── Główny komponent ──────────────────────────────────────────────────────

export function CompanyDataTab({
  contextRenderers,
  renderField,
  formData = {},
}: CompanyDataTabProps) {
  const { t } = useTranslation();

  // Aktywny layout — TEN SAM hook co rodzic: honoruje layout przypięty w pozycji menu
  // (`?mi=` → `layoutId`) i sam nakłada nakładkę per-tenant (`applyTabOverrides`).
  // Metadane pól żyją w silniku (useKartotekaRenderField, rodzic), ten komponent zna
  // tylko strukturę sekcji.
  const layoutQ = useSystemModuleActiveLayout(MODULE_SLUG);

  const { dataTabSections, dataTabExtras } = useMemo(() => {
    // F12.N post-E1.T7: backend zwraca NOWY rekurencyjny kształt
    // (`elements[0].tabs[].children`). Adapter `loadTabsFromConfig` rozpakowuje do starego
    // płaskiego `LayoutTab[]` z polami `sections` + `extras`. Nakładkę per-tenant (ukryte/zwężone
    // pola i formatki) nakłada już hook — tu NIE aplikujemy jej drugi raz.
    const cfg = layoutQ.layoutConfig ?? { elements: [] };
    const tabs = loadTabsFromConfig(cfg);
    const dataTab = tabs.find((tab) => tab.type === 'fields');
    return {
      dataTabSections: (dataTab?.sections ?? []) as LayoutSection[],
      // Elementy „luźne" zakładki Dane spoza sekcji (mirror ContractorDataTab — dziś Firmy nie
      // mają takiego bloku w seedzie, ale adapter jest wspólny, więc trzymamy tę samą obsługę,
      // żeby ewentualny loose block z buildera nie zniknął po cichu).
      dataTabExtras: (dataTab?.extras ?? []) as LayoutElement[],
    };
  }, [layoutQ.layoutConfig]);

  // Renderuje „luźny" element layoutu (blok-widok spoza sekcji) PEŁNĄ szerokością: przez
  // contextRenderers rodzica, a dla elementów bez kontekstu — przez generyczny rejestr
  // (heading/alert/…). Settings elementu lecą do renderera.
  function renderLooseElement(el: LayoutElement): ReactNode {
    const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
      ? contextRenderers?.[el.type]
      : undefined;
    if (contextRenderer) {
      return (
        <Fragment key={el.id}>
          {contextRenderer({ element: { id: el.id, type: el.type, settings: el.settings } })}
        </Fragment>
      );
    }
    const layoutNode = tryRenderLayoutElement({
      element: {
        id: el.id,
        type: el.type,
        settings: (el.settings ?? {}) as Record<string, unknown>,
      },
      children: [],
      mode: 'form',
    });
    return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
  }

  if (layoutQ.isLoading) {
    return (
      <div className="space-y-3 p-4" data-testid="company-data-tab-loading">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  const showLayoutWarning = layoutQ.isError || (!layoutQ.isLoading && dataTabSections.length === 0);

  return (
    <div className="space-y-6" data-testid="company-data-tab">
      {showLayoutWarning && (
        <div
          className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
          data-testid="company-data-tab-no-layout"
        >
          {t('tenant.companies.detail.noLayoutWarning')}
        </div>
      )}

      {dataTabSections.map((section, sectionIdx) => (
        <Fragment key={section.id}>
          <Section title={section.title ?? t('tenant.companies.detail.untitledSection')}>
            {section.elements.map((el) => {
              // Ukrycie przez nakładkę per-tenant (systemowy layout) — pole/element z flagą
              // `hidden` nie renderuje się na karcie (builder pokazuje je wyszarzone).
              if (el.hidden === true) return null;
              // Warunek widoczności z kreatora (`settings.conditionalField/Operator/Value`
              // vs `formData`) — parytet z bogatym rendererem.
              if (!isFieldElementVisible(el, formData)) return null;
              // Non-field elementy (heading/divider/alert/...) renderowane przez
              // LayoutElementRegistry. Elementy zależne od kontekstu (company_members_view/…)
              // muszą iść przez contextRenderers rodzica — dokładnie jak w silniku
              // (RecursiveFormRenderer) i mirror ContractorDataTab.tsx — inaczej renderują się
              // jako martwy placeholder z generycznego rejestru.
              if (el.type !== 'field') {
                const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
                  ? contextRenderers?.[el.type]
                  : undefined;
                if (contextRenderer) {
                  // Bloki-widoki (użytkownicy firmy, załączniki…) to pełnowymiarowe formatki,
                  // nie pojedyncze pole — w gridzie sekcji muszą zajmować cały wiersz.
                  return (
                    <div key={el.id} className="md:col-span-2">
                      {contextRenderer({
                        element: { id: el.id, type: el.type, settings: el.settings },
                      })}
                    </div>
                  );
                }
                const layoutNode = tryRenderLayoutElement({
                  element: {
                    id: el.id,
                    type: el.type,
                    settings: (el.settings ?? {}) as Record<string, unknown>,
                  },
                  children: [],
                  mode: 'form',
                });
                return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
              }

              if (!el.fieldId) return null;
              // Styl pola z kreatora (kolor etykiety/kontrolki) — scoped-CSS wokół
              // `renderField`, wspólny klocek z bogatym rendererem (styled-field).
              return (
                <StyledFieldSlot key={el.id} el={el} formData={formData}>
                  {renderField(el.fieldId, el.settings)}
                </StyledFieldSlot>
              );
            })}
          </Section>
          {/* Luźne elementy zakładki Dane spoza sekcji — patrz komentarz `dataTabExtras` wyżej. */}
          {sectionIdx === 0 && dataTabExtras.map(renderLooseElement)}
        </Fragment>
      ))}
      {/* Nietypowy layout bez żadnej sekcji — luźne elementy i tak pokaż. */}
      {dataTabSections.length === 0 && dataTabExtras.map(renderLooseElement)}
    </div>
  );
}
